<template>
  <div class="starter-container grey lighten-4" style="min-height: 100vh;">
    <!-- Modern Stats Dashboard for Starter -->
    <v-card class="sticky-nav px-4 py-3 mb-4" elevation="1">
      <v-row align="center">
        <!-- Date Selector & Bay Selector -->
        <v-col cols="12" md="4" class="d-flex align-center">
          <v-btn icon color="primary" class="mr-2" @click="$router.push('/booking')" title="Volver al Tee Sheet">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <v-menu ref="menu" v-model="menu" :close-on-content-click="true" transition="scale-transition" offset-y
            min-width="auto">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field v-model="date" prepend-inner-icon="mdi-calendar" readonly v-bind="attrs" v-on="on" outlined
                dense hide-details class="search-field-modern mr-3" style="max-width: 180px;"></v-text-field>
            </template>
            <v-date-picker v-model="date" @change="updateSelectedDate()" no-title @input="menu = false" scrollable
              color="primary"></v-date-picker>
          </v-menu>

          <v-select v-if="salidasDisponibles > 1" v-model="salida" :items="compSalidas" item-value="id"
            item-text="nombre" outlined dense hide-details placeholder="Salida" @input="init()"
            prepend-inner-icon="mdi-golf" class="search-field-modern" style="max-width: 200px;"></v-select>
        </v-col>

        <!-- Stats Chips -->
        <v-col cols="12" md="8" class="d-flex justify-md-end justify-start flex-wrap align-center gap-2">
          <v-chip class="font-weight-bold ma-1" color="primary" outlined>
            <v-icon left size="18">mdi-account-group</v-icon>
            Proyectado: {{ totalProyectado }}
          </v-chip>
          <v-chip class="font-weight-bold ma-1" color="success" outlined>
            <v-icon left size="18">mdi-check-circle</v-icon>
            Pagados: {{ totalPagados }}
          </v-chip>
          <v-chip class="font-weight-bold ma-1" color="orange" outlined>
            <v-icon left size="18">mdi-clock-outline</v-icon>
            Pendientes: {{ totalPendientes }}
          </v-chip>
        </v-col>
      </v-row>
    </v-card>

    <!-- Main Timeline View -->
    <v-container fluid class="px-4 pb-10">
      <div v-if="loadingData" class="d-flex justify-center my-10">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
      </div>

      <div v-else class="starter-timeline">
        <template v-for="(timeData, index) in times">
          <!-- Only show times that have players OR are in the near future (to avoid massive empty lists, but Starter needs to see the flow) -->
          <div :key="index" class="timeline-row mb-6" v-if="shouldDisplayTime(timeData)">
            <!-- Time Indicator -->
            <div class="time-indicator d-flex flex-column align-center mr-6">
              <div class="time-text font-weight-black text-h5"
                :class="isPast(timeData.time) ? 'grey--text' : 'primary--text'">
                {{ timeData.time }}
              </div>
              <div class="time-line" :class="isPast(timeData.time) ? 'bg-grey' : 'bg-primary'"></div>
            </div>

            <!-- Players Cards Container -->
            <div class="players-container flex-grow-1">
              <v-row dense>
                <v-col cols="12" v-if="timeData.players.length === 0">
                  <v-card class="empty-slot-card d-flex align-center justify-center py-4" flat outlined>
                    <span class="text-caption grey--text font-weight-bold">SIN RESERVAS</span>
                  </v-card>
                </v-col>

                <v-col v-for="(player, pIndex) in timeData.players" :key="pIndex" cols="12" sm="6" md="4" lg="3">
                  <v-card class="starter-player-card pa-3" :class="getStatusClass(player)" elevation="1">
                    <!-- Status Header -->
                    <div class="d-flex justify-space-between align-center mb-2">
                      <v-chip x-small class="font-weight-black px-2" :color="getStatusColor(player)" text-color="white">
                        {{ getStatusText(player) }}
                      </v-chip>

                      <div class="d-flex align-center">
                        <span v-if="player.can" class="cart-badge mr-2">
                          <v-icon x-small color="grey darken-3" class="mr-1">mdi-golf-cart</v-icon>
                          {{ player.can }}
                        </span>
                        <span class="text-caption grey--text text--darken-1 font-weight-bold">
                          #{{ player.FolioReserva }}
                        </span>
                      </div>
                    </div>

                    <!-- Player Info -->
                    <div class="player-name text-truncate mb-1 font-weight-black text-h6" :title="player.Nom">
                      {{ player.Nom }}
                    </div>

                    <div class="d-flex flex-column mb-3">
                      <div class="d-flex flex-wrap gap-1 mb-1">
                        <span v-if="player.deda" class="debt-badge">ADEUDO</span>
                        <span v-if="player.bahia && salidasDisponibles > 1" class="text-caption grey--text mr-2">
                          <v-icon x-small>mdi-golf</v-icon> {{ getNombreBahia(player.bahia) }}
                        </span>
                        <span v-if="player.fvta" class="text-caption grey--text">
                          <v-icon x-small>mdi-receipt</v-icon> {{ player.fvta }}
                        </span>
                      </div>

                      <!-- Additional Details (Notes, Tarifa, Handicap) -->
                      <div class="text-caption grey--text text--darken-2 line-height-tight">
                        <div v-if="player.trfr" class="d-flex align-start mt-1">
                          <v-icon x-small class="mr-1 mt-1">mdi-tag-outline</v-icon>
                          <span class="text-truncate">{{ player.trfr }}</span>
                        </div>
                        <div class="d-flex align-start mt-1" :class="{ 'cursor-pointer': player.Notas }"
                          @click="player.Notas ? showNoteDialog(player) : null">
                          <v-icon x-small class="mr-1 mt-1"
                            :color="player.Notas ? 'primary' : ''">mdi-note-text-outline</v-icon>
                          <span class="text-truncate flex-grow-1"
                            :class="{ 'primary--text font-weight-medium': player.Notas }">
                            {{ player.Notas || 'Sin notas' }}
                          </span>
                          <v-icon v-if="player.Notas" x-small color="primary" class="ml-1 mt-1">mdi-open-in-new</v-icon>
                        </div>
                        <div v-if="player.phd" class="d-flex align-start mt-1">
                          <v-icon x-small class="mr-1 mt-1">mdi-podium</v-icon>
                          <span>Handicap: {{ player.phd }}</span>
                        </div>
                      </div>
                    </div>

                    <!-- Actions for Starter -->
                    <v-divider class="mb-2"></v-divider>
                    <div class="d-flex justify-space-between align-center">
                      <!-- Contextual action depending on current status -->
                      <v-btn v-if="player.E === 'P'" small color="success" depressed
                        class="action-btn font-weight-bold">
                        <v-icon left small>mdi-golf-tee</v-icon> TEE OFF

                      </v-btn>

                      <v-btn v-else-if="player.E === 'R'" small color="primary" depressed
                        class="action-btn font-weight-bold">
                        <v-icon left small>mdi-check</v-icon> PENDIENTE CHECK IN
                      </v-btn>

                      <span v-else-if="player.E === 'T'"
                        class="success--text font-weight-bold text-caption d-flex align-center">
                        <v-icon color="success" small class="mr-1">mdi-check-all</v-icon> EN CAMPO
                      </span>


                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </div>
          </div>
        </template>
      </div>
    </v-container>
    <!-- Dialog for Notes -->
    <v-dialog v-model="dialogNote" max-width="400">
      <v-card>
        <v-card-title class="text-h6 pb-2">
          Notas de {{ selectedNoteTitle }}
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4 text-body-1 black--text" style="white-space: pre-wrap;">
          {{ selectedNoteText }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="dialogNote = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from "moment";
import Reserva from "../../clases/Reserva.js";
import { mapGetters } from "vuex";

export default {
  name: "StarterView",
  data() {
    return {
      salida: 1,
      date: moment().format("YYYY-MM-DD"),
      input_date: moment(),
      actualDate: moment(),
      menu: false,
      loadingData: false,
      dialogNote: false,
      selectedNoteTitle: "",
      selectedNoteText: "",
      times: [],
      reservas: [],
      daysRules: [],
      ConfigRva: null,
      MainDepartamento: "",

      // Stats
      totalReservas: 0,
      totalPlayers: 0,
      totalProyectado: 0,
      totalCancelados: 0,
      totalPagados: 0,
      totalNoShow: 0,
      totalPendientes: 0,

      initTime: new moment("06:00", "HH:mm"),
      endTime: new moment("18:00", "HH:mm"),
      configTime: {},
      actualListener: "",
    };
  },
  computed: {
    ...mapGetters(["getSalidas"]),
    compSalidas() {
      let salidas = [];
      this.getSalidas.forEach(bahia => {
        salidas.push({ id: bahia.id, nombre: bahia.nombre });
      });
      salidas.push({ id: "ALL", nombre: "TODOS" });
      return salidas;
    },
    salidasDisponibles() {
      if (this.ConfigRva?.Calendario?.Salidas) {
        return this.ConfigRva.Calendario.Salidas;
      }
      return 1;
    }
  },
  methods: {
    getNombreBahia(id) {
      let salida = this.getSalidas.find((s) => s.id == id);
      return salida ? salida.nombre : "";
    },
    showNoteDialog(player) {
      this.selectedNoteTitle = player.Nom;
      this.selectedNoteText = player.Notas;
      this.dialogNote = true;
    },
    updateSelectedDate() {
      this.input_date = moment(this.date);
      this.init();
    },
    isPast(timeStr) {
      const timeSlot = moment(`${this.date} ${timeStr}`, "YYYY-MM-DD HH:mm");
      return this.actualDate.isAfter(timeSlot);
    },
    shouldDisplayTime(timeData) {
      // For Starter, we want to show times that have players, or upcoming empty slots (e.g. next 2 hours).
      if (timeData.players.length > 0) return true;

      const timeSlot = moment(`${this.date} ${timeData.time}`, "YYYY-MM-DD HH:mm");
      const oneHourAgo = moment(this.actualDate).subtract(1, 'hours');

      return timeSlot.isAfter(oneHourAgo);
    },
    getStatusClass(player) {
      if (player.E === 'P') return 'status-paid';
      if (player.E === 'R') return 'status-pending';
      if (player.E === 'N') return 'status-noshow';
      if (player.E === 'T') return 'status-teedoff';
      return 'status-default';
    },
    getStatusColor(player) {
      if (player.E === 'P') return 'success';
      if (player.E === 'R') return 'orange';
      if (player.E === 'N') return 'grey';
      if (player.E === 'T') return 'blue';
      return 'grey';
    },
    getStatusText(player) {
      if (player.E === 'P') return 'PAGADO';
      if (player.E === 'R') return 'PENDIENTE';
      if (player.E === 'N') return 'NO-SHOW';
      if (player.E === 'T') return 'EN CAMPO';
      if (player.E === 'C') return 'CANCELADO';
      return player.E;
    },
    async updatePlayerStatus(player, newStatus) {
      try {
        this.$swal({
          title: '¿Actualizar estado?',
          text: `El jugador ${player.Nom} cambiará a estado ${this.getStatusText({ E: newStatus })}.`,
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: 'Sí, actualizar',
          cancelButtonText: 'Cancelar'
        }).then(async (result) => {
          if (result.isConfirmed) {
            // Optimistic UI update
            player.E = newStatus;

            // TODO: Replace with actual DB update method if available in backend
            // Example: await this.$store.getters.getFirebaseApp.fba.call("RVA-Reservas-UpdateStatus", { id: player.id, status: newStatus });

            this.$toast.success("Estado actualizado exitosamente (Simulación UI)");
            this.recalculateStats();
          }
        });
      } catch (e) {
        console.error(e);
        this.$toast.error("Error al actualizar estado");
      }
    },
    recalculateStats() {
      this.totalPagados = 0;
      this.totalPendientes = 0;
      this.totalNoShow = 0;

      this.times.forEach(t => {
        t.players.forEach(p => {
          if (p.E === 'P') this.totalPagados++;
          if (p.E === 'R') this.totalPendientes++;
          if (p.E === 'N') this.totalNoShow++;
        });
      });
    },

    async init() {
      this.loadingData = true;
      await this.getDaysRules();
      await this.getReservas();
      this.loadingData = false;
    },
    async getConfig() {
      const conf = await this.$store.getters.getFirebaseApp.database.queryBuilder("RVA/Configuracion/Golf");
      this.ConfigRva = conf.val();
    },
    async getDaysRules() {
      try {
        this.$store.getters.getFirebaseApp.database.queryBuilderListener(
          "RVA/Configuracion/" + this.MainDepartamento + "/Calendario",
          null,
          (snapshot) => {
            if (snapshot.exists()) {
              this.daysRules = [];
              if (snapshot.val().Fechas_Especiales) {
                let arrayCalendar = Object.entries(snapshot.val().Fechas_Especiales);
                arrayCalendar.forEach((element) => {
                  this.daysRules.push({ date: element[0], data: element[1] });
                });
                this.daysRules.sort((a, b) => a.date - b.date);
              }
              this.configTime.initTime = new moment(snapshot.val().Hora_Apertura, "HH:mm:ss");
              this.configTime.endTime = new moment(snapshot.val().Hora_Cierre, "HH:mm:ss");
            }
          }
        );
      } catch (e) {
        console.log(e);
      }
    },
    killListener(date) {
      if (!date) return;
      const ref = this.$store.getters.getFirebaseApp.database.ref("RVA/Reservas/" + this.MainDepartamento + "/" + date);
      this.$store.getters.getFirebaseApp.database.off(ref);
    },
    async getReservas() {
      this.totalReservas = 0;
      let date = this.input_date.format("YYYY-MM-DD");
      this.killListener(this.actualListener);

      this.$store.getters.getFirebaseApp.database.queryBuilderListener(
        "RVA/Reservas/" + this.MainDepartamento + "/" + date,
        null,
        (snapshot) => {
          try {
            if (snapshot.exists()) {
              let arrayReservas = snapshot.val();
              let reservaList = Reserva.parseAvailabilityData(arrayReservas, this.salida, this.getSalidas);

              if (this.salida !== "ALL") {
                this.reservas = reservaList.filter(r => r.bahia == this.salida);
              } else {
                this.reservas = reservaList;
              }

              this.totalPlayers = 0;
              this.totalPagados = 0;
              this.totalPendientes = 0;
              this.totalNoShow = 0;

              this.reservas.forEach((element) => {
                if (element.rvsData) {
                  this.totalPlayers += element.rvsData.length;
                  element.rvsData.forEach((ply) => {
                    if (ply.E === "P") this.totalPagados++;
                    if (ply.E === "R") this.totalPendientes++;
                    if (ply.E === "N") this.totalNoShow++;
                  });
                }
              });
            } else {
              this.reservas = [];
              this.totalPlayers = 0;
              this.totalPagados = 0;
              this.totalPendientes = 0;
            }
          } catch (e) {
            console.log(e);
          }
          this.buildTimesData();
        }
      );
      this.actualListener = date;
    },
    buildTimesData() {
      try {
        let initTim = new moment(this.configTime.initTime || "06:00", "HH:mm");
        let endTime = new moment(this.configTime.endTime || "18:00", "HH:mm");

        let auxDate = initTim.clone();
        this.times = [];
        this.totalProyectado = 0;

        while (auxDate.format("HH:mm") <= endTime.format("HH:mm")) {
          let playersInSlot = [];

          this.reservas.forEach((element) => {
            let time = new moment(element.time, "HH:mm:ss");
            if (time.format("HH:mm") === auxDate.format("HH:mm")) {
              if (element.rvsData) {
                playersInSlot = element.rvsData.filter(p => p.E !== 'C');
              }
            }
          });

          this.totalProyectado += 4;

          this.times.push({
            time: auxDate.format("HH:mm"),
            players: playersInSlot,
          });

          auxDate.add(10, "minutes");
        }
      } catch (e) {
        console.error("Error building times", e);
      }
    }
  },
  async mounted() {
    this.MainDepartamento = localStorage.getItem("GolfDepartment") || "Golf";
    await this.getConfig();

    setInterval(() => {
      this.actualDate = moment();
    }, 60000);

    this.init();
  },
  beforeDestroy() {
    this.killListener(this.actualListener);
  }
};
</script>

<style scoped>
.starter-container {
  font-family: 'Inter', 'Roboto', sans-serif;
}

.sticky-nav {
  position: sticky;
  top: 64px;
  z-index: 6;
  border-radius: 0 0 16px 16px !important;
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(10px);
}

.search-field-modern {
  border-radius: 12px !important;
}

/* Timeline Layout */
.timeline-row {
  display: flex;
  position: relative;
}

.time-indicator {
  width: 80px;
  flex-shrink: 0;
  position: relative;
}

.time-text {
  background: white;
  padding: 4px 8px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  z-index: 2;
  position: relative;
}

.time-line {
  position: absolute;
  top: 40px;
  bottom: -24px;
  left: 50%;
  width: 4px;
  transform: translateX(-50%);
  border-radius: 4px;
  z-index: 1;
}

.bg-primary {
  background-color: var(--v-primary-base);
}

.bg-grey {
  background-color: #e0e0e0;
}

.empty-slot-card {
  border: 2px dashed #e0e0e0 !important;
  background: transparent !important;
  border-radius: 12px;
}

/* Player Cards */
.starter-player-card {
  border-radius: 16px !important;
  border-left: 6px solid #e0e0e0 !important;
  transition: transform 0.2s, box-shadow 0.2s;
  background: white;
}

.starter-player-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1) !important;
}

.status-paid {
  border-left-color: #4CAF50 !important;
}

.status-pending {
  border-left-color: #FF9800 !important;
}

.status-noshow {
  border-left-color: #9E9E9E !important;
  opacity: 0.7;
  background: #f5f5f5;
}

.status-teedoff {
  border-left-color: #2196F3 !important;
}

.cart-badge {
  background: #f5f5f5;
  padding: 2px 8px;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: bold;
  border: 1px solid #e0e0e0;
}

.debt-badge {
  background: #ffebee;
  color: #c62828;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 900;
  letter-spacing: 0.5px;
}

.action-btn {
  border-radius: 8px;
  letter-spacing: 0.5px;
}

.gap-1 {
  gap: 4px;
}

.gap-2 {
  gap: 8px;
}

.line-height-tight {
  line-height: 1.2 !important;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
